import { DocumentFileList } from '@/common/components/DocumentFileList'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import Skeleton from '@/common/components/Skeleton'
import Table from '@/common/components/Table'
import { cn } from '@/common/utils/cn'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { priceSurveyProductsColumns } from '@/features/priceSurvey/constants/priceSurveyProductsColumns.constant'
import { priceSurveyStatusStrategy } from '@/features/priceSurvey/constants/priceSurveyStatusOptions.constant'
import { usePriceSurvey } from '@/features/priceSurvey/hooks/usePriceSurvey'
import { PriceSurveyProductsTransformer } from '@/features/priceSurvey/services/priceSurveyProductsTransformer'
import { ChevronsRightIcon } from 'lucide-react'
import { useParams } from 'react-router-dom'

const Show = () => {
  const { id } = useParams()

  const {
    data: priceSurvey,
    isPending: priceSurveyIsPending,
    error: priceSurveyError = {} as any,
    refetch: refetchPriceSurvey,
  } = usePriceSurvey({ id: id || '' })

  const tableMinHeight = 84 + Math.min(Math.max(1, priceSurvey?.price_survey_products?.length || 1), 3) * 52

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={priceSurveyError?.status}
      refetchFunction={refetchPriceSurvey}
    >
      <div className="flex flex-col gap-y-6 text-white-800">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-x-2">
            <GoBackButton fallback="/price-survey" searchLastRouteFromReference={`/price-survey/${id}`} />

            <div className="flex items-center">
              <p className="text-white-400 font-normal">Pesquisa de preços</p>

              <ChevronsRightIcon strokeWidth={1} className="size-8 text-white-400" />

              {priceSurveyIsPending ? (
                <Skeleton className="w-44" />
              ) : (
                <p>Pesquisa {dateTransformer(priceSurvey?.survey_conducted_at || '')}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-6 overflow-y-auto max-h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados da pesquisa</p>

          <div className="flex flex-col gap-y-2">
            <div>
              <p className="text-sm font-light italic">Status</p>

              {priceSurveyIsPending ? (
                <Skeleton className="w-32 h-7" />
              ) : (
                <p
                  className={cn('text-lg font-bold', {
                    'text-warning': priceSurvey?.status === 'pending',
                    'text-danger-soft': priceSurvey?.status === 'rejected',
                    'text-valid': priceSurvey?.status === 'approved',
                    'text-white-300': priceSurvey?.status === 'disabled',
                  })}
                >
                  {priceSurveyStatusStrategy[priceSurvey?.status as keyof typeof priceSurveyStatusStrategy] || ''}
                </p>
              )}
            </div>

            {priceSurvey?.status === 'rejected' && (
              <div>
                <p className="text-sm font-light italic">Justificativa do Status</p>

                <p className="text-lg font-bold">{priceSurvey.rejection_reason || '--'}</p>
              </div>
            )}

            <div>
              <p className="text-sm font-light italic">Cidade</p>

              {priceSurveyIsPending ? (
                <Skeleton className="w-40 h-7" />
              ) : (
                <p className="text-lg font-bold">
                  {priceSurvey?.cities?.map((city) => city.display_name).join(', ') || ''}
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-light italic">Data da pesquisa</p>

              {priceSurveyIsPending ? (
                <Skeleton className="w-32 h-7" />
              ) : (
                <p className="text-lg font-bold">{dateTransformer(priceSurvey?.survey_conducted_at || '')}</p>
              )}
            </div>
          </div>

          <Table
            style={{ minHeight: `${tableMinHeight}px` }}
            rows={PriceSurveyProductsTransformer.tableTransformer()(priceSurvey?.price_survey_products || [])}
            columns={priceSurveyProductsColumns}
            isLoadingData={priceSurveyIsPending}
            page={1}
            totalPages={1}
          />

          <div className="flex flex-col gap-y-2">
            <p className="text-lg font-bold">Anexos</p>

            <DocumentFileList
              files={
                priceSurvey?.evidences?.map((evidence) => ({
                  id: evidence.id,
                  fileName: getFilenameFromUrl(evidence.url),
                  currentFileUrl: evidence.url,
                })) || []
              }
            />
          </div>
        </div>
      </div>
    </ErrorMessageWrapper>
  )
}

export default Show
